import { createSlice, PayloadAction,createAsyncThunk } from "@reduxjs/toolkit";
import type { Meeting } from "../../../../shared/src/types/Meeting";
import type { FinancialData } from "../../../../shared/src/types/FinancialData";
import type { RootState } from "../../store";

export interface CurrentMeetingState {
  currentMeeting: (Meeting & { financialData: FinancialData | null }) | null;
  loading: boolean;
  error: string | null;
  newDate: string;
  newFamilyId: string;
  newMentorId: string;
  newMeetingNumber: number;
  newLocation: string;
  newAttendees: string[];
}

const initialState: CurrentMeetingState = {
  currentMeeting: null,
  loading: false,
  error: null,
  newDate: '',
  newFamilyId: '',
  newMentorId: '',
  newMeetingNumber: 0,
  newLocation: '',
  newAttendees: [],
};

export const saveMeeting = createAsyncThunk(
  'meetings/saveMeeting',
  async (meetingData: {
    date: string;
    familyId: string;
    mentorId: string;
    meetingNumber: number;
    location: string;
    attendees: string[];
    financialData?: Partial<FinancialData>;
  }, { rejectWithValue }) => {
    try {
      console.log("🚀 Sending meeting data to backend:", meetingData);

      const response = await fetch('/api/meetings/dashboard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meetingData: {
            date: meetingData.date,
            family_id: meetingData.familyId,
            mentor_id: meetingData.mentorId,
            meeting_number: meetingData.meetingNumber,
            location: meetingData.location,
            attendees: meetingData.attendees,
          },
          financialData: meetingData.financialData,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error("❌ Backend error:", result);
        throw new Error(`${result.error || `HTTP ${response.status}`}`);
      }

      console.log("✅ Meeting saved successfully:", result);
      return result.data;
    } catch (error) {
      console.error("❗ Save meeting error:", error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

const meetingsSlice = createSlice({
  name: 'currentMeeting',
  initialState,
  reducers: {
    setLoading(state) {
      state.loading = true;
      state.error = null;
    },
    setMeeting(state, action: PayloadAction<any>) { // שינוי זמני ל-any
      const meeting = action.payload;
      
      console.log("Redux setMeeting - Received meeting:", meeting);

      if (!meeting || typeof meeting !== "object" || !meeting.id) {
        console.error("Redux setMeeting - Invalid meeting data:", meeting);
        state.currentMeeting = null;
        state.error = "Invalid meeting data";
        state.loading = false;
        return;
      }

      // וידוא שהנתונים הפיננסיים בפורמט הנכון
      let financialData = null;
      if (meeting.financialData) {
        financialData = {
          id: meeting.financialData.id || "",
          totalIncome: meeting.financialData.totalIncome || 0,
          totalExpenses: meeting.financialData.totalExpenses || 0,
          currentDebtAmount: meeting.financialData.currentDebtAmount || 0,
          incomeExpenseDifference: meeting.financialData.incomeExpenseDifference || 0,
          oneTimeDebtRepayment: meeting.financialData.oneTimeDebtRepayment || 0,
          monthlyDebtRepayment: meeting.financialData.monthlyDebtRepayment || 0,
          differenceAfterRepayment: meeting.financialData.differenceAfterRepayment || 0,
          shortTermSavings: meeting.financialData.shortTermSavings || 0,
          longTermSavings: meeting.financialData.longTermSavings || 0,
        };
      }

      state.currentMeeting = {
        ...meeting,
        financialData
      };
      
      state.loading = false;
      state.error = null;

      // עדכון השדות החדשים
      state.newDate = meeting.date?.split("T")[0] || '';
      state.newMeetingNumber = meeting.meeting_number || 0;
      state.newLocation = meeting.location || '';
      state.newAttendees = meeting.attendees || [];
      state.newFamilyId = meeting.family_id || '';
      state.newMentorId = meeting.mentor_id || '';

      console.log("Redux setMeeting - Updated state:", state.currentMeeting);
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    updateFinancialData(state, action: PayloadAction<Partial<FinancialData>>) {
      if (state.currentMeeting && state.currentMeeting.financialData) {
        state.currentMeeting.financialData = {
          ...state.currentMeeting.financialData,
          ...action.payload,
        };
      }
    },
    setNewDate(state, action: PayloadAction<string>) {
      state.newDate = action.payload;
    },
    setNewFamilyId(state, action: PayloadAction<string>) {
      state.newFamilyId = action.payload;
    },
    setNewMentorId(state, action: PayloadAction<string>) {
      state.newMentorId = action.payload;
    },
    setNewMeetingNumber(state, action: PayloadAction<number>) {
      state.newMeetingNumber = action.payload;
    },
    resetForm(state) {
      state.newDate = '';
      state.newFamilyId = '';
      state.newMentorId = '';
      state.newMeetingNumber = 0;
    },
    setNewLocation(state, action: PayloadAction<string>) {
      state.newLocation = action.payload;
    },
    setNewAttendees(state, action: PayloadAction<string[]>) {
      state.newAttendees = action.payload;
    },
  },
});

export const {
  setLoading,
  setMeeting,
  setError,
  updateFinancialData,
  setNewDate,
  setNewFamilyId,
  setNewMentorId,
  setNewMeetingNumber,
  resetForm,
  setNewLocation,
  setNewAttendees,
} = meetingsSlice.actions;

export default meetingsSlice.reducer;

// Selectors
export const selectCurrentMeeting = (state: RootState) =>
  state.meetings?.currentMeeting ?? null;

export const selectMeetingLoading = (state: RootState) =>
  state.meetings?.loading ?? false;

export const selectMeetingError = (state: RootState) =>
  state.meetings?.error ?? null;

export const selectNewDate = (state: RootState) =>
  state.meetings.newDate;

export const selectNewFamilyId = (state: RootState) =>
  state.meetings.newFamilyId;

export const selectNewMentorId = (state: RootState) =>
  state.meetings.newMentorId;

export const selectNewMeetingNumber = (state: RootState) =>
  state.meetings.newMeetingNumber;

export const selectNewLocation = (state: RootState) =>
  state.meetings.newLocation;

export const selectNewAttendees = (state: RootState) =>
  state.meetings.newAttendees;
